import { useEffect, useState } from "react";
import { Loader2, Pencil, Radio, RotateCcw, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DISC_LABEL, STATUS, errMsg, niceDate, pill } from "@/lib/scoring";

function DeleteDialog({ api, target, onClose, onDone }) {
  const [view, setView] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { setView(null); setErr(""); if (target) api.get(`/scoring/sessions/${target.id}`).then(setView); }, [api, target]);
  const go = async (e) => { e.preventDefault(); try { await api.del(`/admin/scoring/sessions/${target.id}`); onDone(); } catch (x) { setErr(errMsg(x)); } };
  return (
    <AlertDialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent data-testid="delete-session-dialog" className="bg-white text-[#0B0F19]">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete “{target?.name}”?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-sm text-slate-600 space-y-2">
              <p>{target && `${DISC_LABEL[target.discipline]} · ${niceDate(target.date)} · ${target.reps} scored attempts.`} Its results leave every leaderboard and player stat. You can restore it from <b>Deleted</b>.</p>
              <p className="font-semibold text-[#0B0F19]">Affected players:</p>
              <p data-testid="delete-affected">{view ? view.leaderboard.filter((r) => r.reps > 0).map((r) => `${r.name} (${r.reps})`).join(", ") || "None" : "Loading…"}</p>
              {err && <p className="text-red-600 font-semibold">{err}</p>}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="delete-cancel">Keep session</AlertDialogCancel>
          <AlertDialogAction data-testid="delete-confirm" onClick={go} className="bg-red-600 hover:bg-red-700 text-white">Delete session</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Session list. mode "active" = drafts + in progress for scoring; "manage" = full history with edit / delete / restore.
export default function SessionList({ api, onOpen, onStart, admin, mode }) {
  const filters = mode === "active" ? [["", "All open"], ["draft", "Draft"], ["live", "In Progress"]] : [["", "All"], ["closed", "Completed"], ["live", "In Progress"], ["draft", "Draft"], ...(admin ? [["deleted", "Deleted"]] : [])];
  const [st, setSt] = useState("");
  const [list, setList] = useState(null);
  const [del, setDel] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    setList(null);
    api.get("/scoring/sessions", { status: st }).then((d) => setList(mode === "active" && !st ? d.sessions.filter((s) => s.status !== "closed") : d.sessions));
  }, [api, st, mode, tick]);
  const restore = async (s) => { await api.post(`/admin/scoring/sessions/${s.id}/restore`, {}); setTick((t) => t + 1); };
  return (
    <div data-testid={`sessions-${mode}`}>
      {mode === "active" && <button data-testid="new-session-btn" onClick={onStart} className="mb-5 rounded-full bg-[#2563EB] hover:bg-[#1d4fd7] px-6 py-3 text-sm font-bold text-white transition-colors">Start Session</button>}
      <div className="flex flex-wrap gap-2">{filters.map(([k, l]) => <button key={k} data-testid={`sessions-filter-${k || "all"}`} onClick={() => setSt(k)} className={pill(st === k)}>{l}</button>)}</div>
      {!list && <Loader2 className="mt-6 animate-spin text-slate-400" />}
      {list && !list.length && <p data-testid="sessions-empty" className="mt-6 text-sm text-slate-500">{mode === "active" ? "No open sessions. Start one to begin scoring." : "No sessions here."}</p>}
      <div className="mt-5 space-y-2">
        {(list || []).map((s) => (
          <div key={s.id} data-testid={`session-${s.id}`} className="rounded-2xl border border-slate-200 bg-white hover:border-[#2563EB] p-4 transition-colors flex items-center justify-between gap-3">
            <button data-testid={`open-session-${s.id}`} onClick={() => onOpen(s.id)} className="flex-1 min-w-0 text-left">
              <span className="block font-bold text-[#0B0F19] break-words">{s.name}</span>
              <span className="block text-xs text-slate-500 mt-1">{DISC_LABEL[s.discipline]} · {niceDate(s.date)} · {s.age_group ? `${s.age_group} · ` : ""}{s.player_ids.length} athletes · {s.reps} attempts · avg {s.session_avg}</span>
            </button>
            <span className="flex items-center gap-3 shrink-0">
              {s.deleted ? <span className="text-xs font-bold text-red-600">Deleted</span> : s.status === "live" ? <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-600"><Radio size={13} /> In Progress</span> : <span className="text-xs font-bold text-slate-500">{STATUS[s.status]}</span>}
              {admin && mode === "manage" && !s.deleted && <>
                <button data-testid={`edit-session-${s.id}`} onClick={() => onOpen(s.id)} className="text-slate-400 hover:text-[#0B0F19]" aria-label="Edit session"><Pencil size={15} /></button>
                <button data-testid={`delete-session-${s.id}`} onClick={() => setDel(s)} className="text-slate-400 hover:text-red-600" aria-label="Delete session"><Trash2 size={15} /></button>
              </>}
              {admin && s.deleted && <button data-testid={`restore-session-${s.id}`} onClick={() => restore(s)} className="inline-flex items-center gap-1 text-xs font-bold text-[#2563EB]"><RotateCcw size={13} /> Restore</button>}
            </span>
          </div>
        ))}
      </div>
      <DeleteDialog api={api} target={del} onClose={() => setDel(null)} onDone={() => { setDel(null); setTick((t) => t + 1); }} />
    </div>
  );
}
